import { ProjectDot } from "@/components/projects/project-color";
import {
  type TimeEntriesResponse,
  type TimeEntry,
  type TimeEntrySummary,
  approvalLabel,
} from "@/components/time-entries/list/types";
import { MyTimeSummary } from "@/components/time-entries/mine/summary";
import { AppLink } from "@/components/ui/app-link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Pagination } from "@/components/ui/pagination";
import { Select } from "@/components/ui/select";
import { Tabs } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { type DateRange, addDays } from "@/lib/date-range";
import { formatWallDate, isoToPragueDate, pragueWallToIso, todayPragueDate } from "@/lib/datetime";
import { projectHref } from "@/lib/project-href";
import { paginationSearchFields } from "@/lib/search";
import { formatHours } from "@/lib/utils";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";

/**
 * `/time-entries` — **your own** time.
 *
 * The full list (`/time-entries-all`) exists to run the team's time: bulk
 * approvals, cross-user selection, billing states, the admin edit modal, the
 * discarded-triage lane. All of that is machinery for deciding about *other
 * people's* hours, and it buries the one question this page answers — what did
 * I log, and where did it land?
 *
 * So this is deliberately a fraction of it: your rows, two filters, no
 * selection, no bulk bars, nothing that mutates. Admins get it too, and see
 * exactly what everyone else sees — that's the point.
 *
 * `from` / `to` in the URL are Prague days (`YYYY-MM-DD`); older links carry
 * ISO instants, which still read as the day they fall on.
 */

const searchSchema = z.object({
  view: z.enum(["summary", "detailed"]).optional(),
  projectId: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  ...paginationSearchFields,
});

type Search = z.infer<typeof searchSchema>;

function toDay(value: string | undefined): string | undefined {
  if (!value) return undefined;
  return value.length === 10 ? value : isoToPragueDate(value);
}

/** The filter as the API reads it: `scope=mine` + instants bounding the days. */
function apiQuery(
  search: Pick<Search, "projectId" | "from" | "to">,
  extra: Record<string, string> = {},
): string {
  const qs = new URLSearchParams({ scope: "mine", ...extra });
  const from = toDay(search.from);
  const to = toDay(search.to);
  if (search.projectId) qs.set("projectId", search.projectId);
  if (from) qs.set("from", pragueWallToIso(`${from}T00:00`));
  if (to) qs.set("to", pragueWallToIso(`${to}T23:59`));
  return qs.toString();
}

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const APPROVAL_VARIANT: Record<string, "default" | "secondary" | "success" | "outline"> = {
  worked: "outline",
  budget: "secondary",
  approved: "success",
  paid: "default",
  rejected: "secondary",
};

export const Route = createFileRoute("/_app/time-entries/")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({
    view: search.view,
    projectId: search.projectId,
    from: search.from,
    to: search.to,
    page: search.page,
    pageSize: search.pageSize,
  }),
  loader: async ({ deps }) => {
    if (deps.view === "detailed") {
      const extra: Record<string, string> = {};
      if (deps.page) extra.page = String(deps.page);
      if (deps.pageSize) extra.pageSize = String(deps.pageSize);
      return {
        view: "detailed" as const,
        list: await api.get<TimeEntriesResponse>(`/api/time-entries?${apiQuery(deps, extra)}`),
      };
    }
    // The two charts ignore the page's filters: they're a fixed look back.
    const today = todayPragueDate();
    const [range, week, month] = await Promise.all([
      api.get<TimeEntrySummary>(`/api/time-entries/summary?${apiQuery(deps)}`),
      api.get<TimeEntrySummary>(
        `/api/time-entries/summary?${apiQuery({ from: addDays(today, -6), to: today })}`,
      ),
      api.get<TimeEntrySummary>(
        `/api/time-entries/summary?${apiQuery({ from: addDays(today, -29), to: today })}`,
      ),
    ]);
    return { view: "summary" as const, range, week, month };
  },
  component: MyTimeEntriesPage,
});

/** The project link, or null for a row whose project isn't GitLab-bound. */
function hrefFor(project: NonNullable<TimeEntry["project"]>): string | null {
  try {
    return projectHref(project);
  } catch {
    return null;
  }
}

function MyTimeEntriesPage() {
  const { t } = useTranslation();
  const data = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const view = search.view ?? "summary";
  const from = toDay(search.from);
  const to = toDay(search.to);
  const range: DateRange | null = from && to ? { from, to } : null;

  function setFilter(patch: Partial<Search>) {
    // Any filter change returns to page 1 — staying on page 4 of a narrower
    // result is how you end up staring at an empty table.
    navigate({ to: "/time-entries", search: { ...search, ...patch, page: undefined } });
  }

  return (
    <div className="w-full space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">{t("timeEntries.mine.title")}</h1>
          <p className="text-muted-foreground mt-1">
            <Trans
              i18nKey="timeEntries.mine.subtitle"
              components={{ 1: <Link to="/suggestions/time-entries" className="underline" /> }}
            />
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild size="sm" variant="outline">
            <a href={`/api/time-entries/export?${apiQuery(search)}`} download>
              {t("timeEntries.common.downloadCsv")}
            </a>
          </Button>
          <Button asChild size="sm" variant="outline">
            <a href={`/api/time-entries/pdf?${apiQuery(search)}`}>
              {t("timeEntries.common.downloadPdf")}
            </a>
          </Button>
          <Button asChild size="sm">
            <Link to="/time-entries/new">{t("timeEntries.mine.logTime")}</Link>
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-card p-2">
        <DateRangePicker value={range} onChange={(r) => setFilter({ from: r?.from, to: r?.to })} />
        <Select
          aria-label={t("timeEntries.filters.byProject")}
          value={search.projectId ?? ""}
          onChange={(v) => setFilter({ projectId: v || undefined })}
          options={[
            { value: "", label: t("timeEntries.filters.allProjects") },
            ...(data.view === "detailed" ? data.list.projects : data.range.projects).map((p) => ({
              value: p.id,
              label: p.name,
            })),
          ]}
        />
        {(search.projectId || search.from || search.to) && (
          <button
            type="button"
            data-testid="my-te-clear"
            onClick={() => navigate({ to: "/time-entries", search: { view: search.view } })}
            className="h-9 px-2 text-sm text-muted-foreground underline hover:text-foreground"
          >
            {t("timeEntries.common.clear")}
          </button>
        )}
      </div>

      <Tabs
        value={view}
        onValueChange={(id) =>
          navigate({
            to: "/time-entries",
            search: { ...search, view: id === "summary" ? undefined : "detailed", page: undefined },
          })
        }
        tabs={[
          {
            id: "summary",
            label: t("timeEntries.mine.tabs.summary"),
            content:
              data.view === "summary" ? (
                <MyTimeSummary range={data.range} week={data.week} month={data.month} />
              ) : null,
          },
          {
            id: "detailed",
            label: t("timeEntries.mine.tabs.detailed"),
            content:
              data.view === "detailed" ? (
                <DetailedEntries list={data.list} search={search} />
              ) : null,
          },
        ]}
      />
    </div>
  );
}

function DetailedEntries({ list, search }: { list: TimeEntriesResponse; search: Search }) {
  const { t } = useTranslation();
  const { entries, total, totalHours, page, pageSize } = list;

  const columns: Array<DataTableColumn<TimeEntry>> = [
    {
      key: "spentOn",
      header: t("timeEntries.mine.date"),
      headClassName: "w-28",
      sortAccessor: (te) => te.spentOn,
      cell: (te) => (
        <span className="whitespace-nowrap text-sm">{formatWallDate(te.spentOn.slice(0, 10))}</span>
      ),
    },
    {
      key: "project",
      header: t("timeEntries.columns.project"),
      headClassName: "w-52",
      sortAccessor: (te) => te.project?.name ?? te.externalProjectName ?? "",
      cell: (te) =>
        te.project ? (
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <ProjectDot projectId={te.project.id} />
            {hrefFor(te.project) ? (
              <AppLink href={hrefFor(te.project) as string} className="truncate hover:underline">
                {te.project.name}
              </AppLink>
            ) : (
              <span className="truncate">{te.project.name}</span>
            )}
          </span>
        ) : (
          <span className="text-xs italic text-muted-foreground">
            {te.externalProjectName
              ? t("timeEntries.common.clockify", { name: te.externalProjectName })
              : t("timeEntries.common.noProject")}
          </span>
        ),
    },
    {
      key: "target",
      header: t("timeEntries.common.workItem"),
      // `max-w-0` lets the two text columns share what's left and truncate, instead of widening the table.
      headClassName: "w-[55%]",
      cellClassName: "max-w-0",
      cell: (te) =>
        te.workItem ? (
          <Link
            to="/work-item/$id"
            params={{ id: String(te.workItem.globalId) }}
            className="block truncate text-sm hover:underline"
            title={te.workItem.title}
          >
            {te.workItem.title}
          </Link>
        ) : te.overhead ? (
          <span className="text-sm text-muted-foreground">{te.overhead.name}</span>
        ) : (
          <span className="text-xs italic text-muted-foreground">—</span>
        ),
    },
    {
      key: "description",
      header: t("timeEntries.columns.description"),
      headClassName: "w-[45%]",
      cellClassName: "max-w-0",
      cell: (te) => (
        <span className="block truncate text-sm text-muted-foreground" title={te.description ?? ""}>
          {te.description || "—"}
        </span>
      ),
    },
    {
      key: "hours",
      header: t("timeEntries.columns.hours"),
      headClassName: "w-24 text-right",
      cellClassName: "text-right tabular-nums",
      sortAccessor: (te) => Number(te.hours),
      cell: (te) => formatHours(Number(te.hours)),
    },
    {
      key: "approval",
      header: t("timeEntries.mine.state"),
      headClassName: "w-32",
      sortAccessor: (te) => te.approvalState,
      // Approval only. Billing state is what the customer gets charged, which
      // is not this page's business and not this reader's decision.
      cell: (te) => (
        <Badge variant={APPROVAL_VARIANT[te.approvalState] ?? "outline"}>
          {approvalLabel(te.approvalState)}
        </Badge>
      ),
    },
  ];

  return (
    <div className="space-y-3">
      <div className="text-right text-sm text-muted-foreground" data-testid="my-te-total">
        {t("timeEntries.mine.summary", { hours: formatHours(Number(totalHours)), count: total })}
      </div>

      <DataTable
        columns={columns}
        rows={entries}
        rowKey={(te) => te.id}
        rowTestId={() => "my-te-row"}
        emptyState={t("timeEntries.mine.empty")}
      />

      <Pagination
        total={total}
        page={page}
        pageSize={pageSize}
        pageSizes={PAGE_SIZE_OPTIONS}
        storageKey="my-time-entries"
        basePath="/time-entries"
        searchParams={{
          view: "detailed",
          projectId: search.projectId,
          from: search.from,
          to: search.to,
        }}
      />
    </div>
  );
}
